import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { LANGS } from "@/lib/i18n";

/**
 * Compact language switcher — a globe pill that opens a scrollable dropdown of the 8 supported locales.
 * Persists to localStorage via the i18n bootstrap and updates <html lang="…"> automatically.
 */
export default function LanguageSwitcher({ align = "right", compact = false }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const current = LANGS.find((l) => l.code === i18n.language) || LANGS[0];

  return (
    <div className="relative inline-flex" ref={ref} data-testid="lang-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        className="fl-btn-ghost text-xs flex items-center gap-1.5"
        title="Language"
        data-testid="lang-switcher-btn"
      >
        <Globe size={13} />
        {compact ? <span className="text-sm">{current.flag}</span> : (
          <span className="uppercase tracking-wider">{current.code}</span>
        )}
      </button>
      {open && (
        <div
          className={`absolute top-full mt-2 fl-card p-1 z-50 min-w-[180px] max-h-[320px] overflow-y-auto ${align === "right" ? "right-0" : "left-0"}`}
          data-testid="lang-switcher-menu"
          style={{ boxShadow: "var(--fl-shadow)" }}
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => { i18n.changeLanguage(l.code); setOpen(false); }}
              className="w-full text-left px-3 py-2 rounded flex items-center gap-2 text-sm hover:bg-white/5"
              style={{ color: "var(--fl-text)" }}
              data-testid={`lang-${l.code}`}
            >
              <span className="text-base">{l.flag}</span>
              <span className="flex-1">{l.label}</span>
              {l.code === current.code && <Check size={13} className="text-[#f47a20]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
